import { useEffect } from "react";

import { MULTISIG_FEATURE_FLAGS } from "@/constants/multisigFeatureFlags";

import { useUnenrollmentStatus } from "@/apiQueries/multisigUnenrollment";

import { formatUnenrollmentDeadline } from "@/helpers/unenrollmentDeadline";
import {
  getUnenrollmentOutcome,
  inferVanishedOutcome,
  recordUnenrollmentOutcome,
  rememberPause,
  takeRememberedPause,
} from "@/helpers/unenrollmentOutcome";
import { formatActiveSignerName } from "@/helpers/unenrollmentView";
import type { UnenrollmentView } from "@/helpers/unenrollmentView";

import { useIsMultisigFeatureEnabled } from "@/hooks/useIsMultisigFeatureEnabled";
import { useRedux } from "@/hooks/useRedux";

export type CoSigningPause = {
  txId: string;
  initiatorUserId: string | null;
  /** The initiator's name from the roster, or a neutral fallback. */
  initiatorName: string;
  initiatorFirstName: string;
  isInitiator: boolean;
  expiresAt: string | null;
  /** "Sunday, Oct 4, 2026 at 2:15 PM EDT" */
  deadlineLong: string | null;
  /** "Oct 4, 2026 at 2:15 PM" */
  deadlineShort: string | null;
  hasMySignature: boolean;
  canSign: boolean;
  view: Extract<UnenrollmentView, { kind: "in-progress" }>;
};

export type CoSigningPauseState = {
  /** A removal is pending and still inside its deadline. */
  isPaused: boolean;
  pause: CoSigningPause | null;
  isLoading: boolean;
};

const NOT_PAUSED: CoSigningPauseState = { isPaused: false, pause: null, isLoading: false };

/**
 * The organization-wide "co-signing pause": a turn-off request that is
 * pending and not yet expired. Every screen that must freeze an action
 * during the pause (New disbursement, co-signer management, Change key)
 * reads this one hook instead of the removal query directly.
 *
 * Limits of the current backend, degraded on purpose rather than guessed:
 *  - `GET /multisig/unenrollment` and `GET /users` are served to Owners only
 *    (backend `serve.go`, `anyRoleMW(OwnerUserRole)`), so this hook does not
 *    request them for any other role, and every non-Owner role (Financial
 *    Controller, Initiator, Approver, Business) always reads "not paused".
 *    The freeze this hook drives is therefore Owner-only in the UI, and the
 *    backend does not enforce it either; the organization-wide freeze needs
 *    both a server-side check on disbursement creation and a pause signal
 *    every role may read. Once the backend opens those reads, widen the role
 *    gate in `useUnenrollmentStatus` to match and bring back the design's
 *    Financial Controller pause banner and outcome copy.
 *  - A removal that has ended is no longer served, so the hook remembers the
 *    pause it last saw and records an inferred outcome when it disappears.
 */
export const useCoSigningPause = (): CoSigningPauseState => {
  const isOptOutEnabled = useIsMultisigFeatureEnabled(MULTISIG_FEATURE_FLAGS.OPT_OUT);
  const { profile } = useRedux("profile");
  const userId = profile.data.id;
  const role = isOptOutEnabled ? (profile.data.role ?? null) : null;

  const { data, isLoading } = useUnenrollmentStatus({ userId, role });

  const inProgress = data?.kind === "in-progress" ? data : null;
  const live = inProgress && !inProgress.isExpired ? inProgress : null;

  useEffect(() => {
    if (!data) return;
    if (live) {
      rememberPause({ txId: live.tx.tx_id, expiresAt: live.expiresAt });
      return;
    }
    if (inProgress?.isExpired) {
      recordUnenrollmentOutcome({
        kind: "expired",
        txId: inProgress.tx.tx_id,
        actor: null,
        byMe: false,
      });
      takeRememberedPause();
      return;
    }
    const remembered = takeRememberedPause();
    if (!remembered) return;
    if (getUnenrollmentOutcome()?.txId === remembered.txId) return;
    recordUnenrollmentOutcome({
      kind: inferVanishedOutcome(remembered),
      txId: remembered.txId,
      actor: null,
      byMe: false,
    });
  }, [data, live, inProgress]);

  if (!isOptOutEnabled) return NOT_PAUSED;

  if (!live) {
    return { isPaused: false, pause: null, isLoading };
  }

  const initiator = live.initiator;
  const initiatorName = formatActiveSignerName(initiator, "An Owner");

  return {
    isPaused: true,
    isLoading: false,
    pause: {
      txId: live.tx.tx_id,
      initiatorUserId: live.initiatorUserId,
      initiatorName,
      initiatorFirstName: initiator?.first_name?.trim() || initiatorName,
      isInitiator: live.isInitiator,
      expiresAt: live.expiresAt,
      deadlineLong: formatUnenrollmentDeadline(live.expiresAt, { style: "long" }),
      deadlineShort: formatUnenrollmentDeadline(live.expiresAt, { style: "short" }),
      hasMySignature: live.hasMySignature,
      canSign: live.canSign,
      view: live,
    },
  };
};
